<script setup lang="ts">
import { useDocumentVisibility, useIntersectionObserver, usePreferredReducedMotion } from '@vueuse/core'

type SegmentTone = 'amber' | 'teal' | 'red'
type Phase = 'typing' | 'idle' | 'press' | 'send' | 'erasing'

type PromptPhrase = {
  text: string
  segments?: SegmentTone[]
}

const PHRASES: PromptPhrase[] = [
  { text: 'Aide moi à faire revenir mes clients', segments: ['amber', 'teal', 'red'] },
  { text: 'Analyse les performances de mon programme de fidélité' },
  { text: 'Analyse les performances de ma dernière campagne SMS' }
]

const SEGMENTS: Record<SegmentTone, { label: string; bg: string; border: string; fg: string }> = {
  amber: { label: 'Ponctuel', bg: '#FCEBBE', border: '#E8A33D', fg: '#A8701F' },
  teal: { label: 'VIP', bg: '#C9F5E1', border: '#46C79A', fg: '#1B8163' },
  red: { label: 'Inactif', bg: '#F8DADA', border: '#C45C5C', fg: '#A34848' }
}

const props = withDefaults(
  defineProps<{
    charsPerSecond?: number
  }>(),
  { charsPerSecond: 28 }
)

const host = ref<HTMLElement>()
const field = ref<HTMLElement>()

const typed = ref('')
const phase = ref<Phase>('typing')
const segment = ref<SegmentTone | null>(null)
const segmentOut = ref(false)
const segmentRun = ref(false)

const visibility = useDocumentVisibility()
const isInView = ref(true)
useIntersectionObserver(
  host,
  ([entry]) => {
    isInView.value = entry.isIntersecting
  },
  { threshold: 0.01 }
)
const isRunning = computed(() => visibility.value === 'visible' && isInView.value)

const reducedMotion = usePreferredReducedMotion()

const hasText = computed(() => typed.value.length > 0)
const activeSegment = computed(() => (segment.value ? SEGMENTS[segment.value] : null))
const isCaretHidden = computed(() => segment.value !== null || segmentRun.value)

const isCaretBlinking = computed(
  () =>
    !isCaretHidden.value && reducedMotion.value !== 'reduce' && phase.value !== 'typing' && phase.value !== 'erasing'
)

const isScrolled = ref(false)

watch(
  [typed, segment],
  () => {
    if (!field.value) return
    field.value.scrollLeft = field.value.scrollWidth
    isScrolled.value = field.value.scrollLeft > 0
  },
  { flush: 'post' }
)

const commonPrefix = (a: string, b: string) => {
  let index = 0
  while (index < a.length && index < b.length && a[index] === b[index]) index++
  return index
}

let runId = 0
let timer: ReturnType<typeof setTimeout> | undefined

const sleep = (id: number, ms: number) =>
  new Promise<void>((resolve) => {
    const arm = () => {
      timer = setTimeout(
        () => {
          if (id !== runId) return
          if (isRunning.value) resolve()
          else arm()
        },
        isRunning.value ? ms : 200
      )
    }
    arm()
  })

const playPhrase = async (id: number, phrase: PromptPhrase, nextText: string, isLast: boolean) => {
  const step = 1000 / Math.max(6, props.charsPerSecond)

  phase.value = 'typing'
  segment.value = null
  segmentOut.value = false
  for (let i = typed.value.length + 1; i <= phrase.text.length; i++) {
    if (id !== runId) return
    typed.value = phrase.text.slice(0, i)
    const char = phrase.text[i - 1]
    await sleep(id, char === ' ' ? step * 1.7 : step * (0.75 + Math.random() * 0.6))
  }
  phase.value = 'idle'
  await sleep(id, 420)

  const segments = phrase.segments ?? []
  if (segments.length) segmentRun.value = true
  for (let s = 0; s < segments.length; s++) {
    if (id !== runId) return
    segment.value = segments[s]
    segmentOut.value = false
    await sleep(id, 1150)
    segmentOut.value = true
    await sleep(id, 240)
    segment.value = null
    segmentOut.value = false
    await sleep(id, s < segments.length - 1 ? 110 : 220)
  }
  if (segments.length) segmentRun.value = false
  await sleep(id, isLast ? 520 : 320)

  if (isLast) {
    phase.value = 'press'
    await sleep(id, 150)
    phase.value = 'send'
    await sleep(id, 620)
  }

  phase.value = 'erasing'
  const keep = commonPrefix(phrase.text, nextText)
  for (let i = phrase.text.length; i >= keep; i--) {
    if (id !== runId) return
    typed.value = phrase.text.slice(0, i)
    await sleep(id, 16)
  }
  phase.value = 'idle'
  await sleep(id, 300)
}

const stop = () => {
  runId++
  clearTimeout(timer)
}

const start = async () => {
  const id = ++runId
  typed.value = ''
  while (id === runId) {
    for (let i = 0; i < PHRASES.length; i++) {
      if (id !== runId) return
      const next = PHRASES[(i + 1) % PHRASES.length].text
      await playPhrase(id, PHRASES[i], next, i === PHRASES.length - 1)
    }
  }
}

onMounted(() => {
  watch(
    reducedMotion,
    (mode) => {
      stop()
      if (mode === 'reduce') {
        typed.value = PHRASES[0]?.text ?? ''
        phase.value = 'idle'
        segment.value = null
        segmentRun.value = false
        return
      }
      start()
    },
    { immediate: true }
  )
})

onBeforeUnmount(stop)
</script>

<template>
  <div ref="host" aria-hidden="true" class="flex w-full justify-center">
    <div
      class="pt-bar flex min-h-[76px] w-full max-w-[720px] scale-[0.8] items-center gap-3 overflow-hidden rounded-[26px] bg-white py-3 pl-[26px] pr-3"
    >
      <div
        ref="field"
        class="pt-field flex min-w-0 flex-1 items-center gap-2.5 self-stretch overflow-hidden"
        :class="{ 'pt-field--scrolled': isScrolled }"
      >
        <span class="whitespace-pre text-lg leading-[1.3] tracking-[-0.01em] text-cta">{{ typed }}</span>

        <span
          class="pt-caret h-[22px] w-0.5 flex-none rounded-[1px] bg-cta transition-opacity duration-[180ms] ease-[cubic-bezier(0.4,0,0.2,1)]"
          :class="{ 'pt-caret--blink': isCaretBlinking }"
          :style="{ opacity: isCaretHidden ? 0 : 1 }"
        />

        <span
          v-if="activeSegment"
          :key="segment ?? ''"
          class="pt-chip -ml-2.5 inline-flex flex-none items-center gap-1.5 whitespace-nowrap rounded-full border-[1.5px] py-[7px] pl-[11px] pr-[13px] text-sm font-medium tracking-[-0.005em]"
          :class="segmentOut ? 'pt-chip--out' : 'pt-chip--in'"
          :style="{
            background: activeSegment.bg,
            borderColor: activeSegment.border,
            color: activeSegment.fg
          }"
        >
          <svg v-if="segment === 'amber'" width="15" height="15" viewBox="0 0 24 24" fill="none">
            <circle cx="10" cy="7.5" r="3.6" fill="#E8A33D" fill-opacity="0.28" stroke="#B4762A" stroke-width="1.7" />
            <path
              d="M3.6 20c0-3.4 2.9-5.6 6.4-5.6 1.5 0 2.9.4 4 1.1"
              stroke="#B4762A"
              stroke-width="1.7"
              stroke-linecap="round"
            />
            <path
              d="M17.4 14.2l1.1 2.3 2.5.36-1.8 1.78.42 2.5-2.22-1.2-2.23 1.2.43-2.5-1.8-1.78 2.5-.36z"
              fill="#E8A33D"
              fill-opacity="0.35"
              stroke="#B4762A"
              stroke-width="1.4"
              stroke-linejoin="round"
            />
          </svg>

          <svg v-else-if="segment === 'teal'" width="15" height="15" viewBox="0 0 24 24" fill="none">
            <circle cx="10" cy="7.5" r="3.6" fill="#46C79A" fill-opacity="0.3" stroke="#1B8163" stroke-width="1.7" />
            <path
              d="M3.6 20c0-3.4 2.9-5.6 6.4-5.6 1.5 0 2.9.4 4 1.1"
              stroke="#1B8163"
              stroke-width="1.7"
              stroke-linecap="round"
            />
            <path
              d="M15 19.4h6M15 19.4l1-4.4 2 2 1.5-2.6 1.5 5z"
              fill="#46C79A"
              fill-opacity="0.35"
              stroke="#1B8163"
              stroke-width="1.4"
              stroke-linejoin="round"
              stroke-linecap="round"
            />
          </svg>

          <svg v-else width="15" height="15" viewBox="0 0 24 24" fill="none">
            <circle cx="10" cy="7.5" r="3.6" fill="#C45C5C" fill-opacity="0.28" stroke="#A34848" stroke-width="1.7" />
            <path
              d="M3.6 20c0-3.4 2.9-5.6 6.4-5.6 1.5 0 2.9.4 4 1.1"
              stroke="#A34848"
              stroke-width="1.7"
              stroke-linecap="round"
            />
            <path d="M18 13.4v4.2M18 20.4h.01" stroke="#A34848" stroke-width="1.8" stroke-linecap="round" />
            <circle cx="18" cy="17" r="5.4" stroke="#A34848" stroke-width="1.4" fill="#C45C5C" fill-opacity="0.18" />
          </svg>

          {{ activeSegment.label }}
        </span>
      </div>

      <div
        class="pt-send grid size-14 flex-none place-items-center overflow-hidden rounded-[18px] bg-cta"
        :class="{ 'pt-send--active': hasText }"
        :style="{ transform: phase === 'press' ? 'scale(0.95)' : 'scale(1)', opacity: hasText ? 1 : 0.3 }"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          class="pt-arrow"
          :class="{ 'pt-arrow--fly': phase === 'send' }"
        >
          <path
            d="M12 19.5V5M12 5l-6.2 6.2M12 5l6.2 6.2"
            stroke="#FFFFFF"
            stroke-width="2.1"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>
    </div>
  </div>
</template>

<style scoped>
.pt-bar {
  box-shadow:
    0 1px 2px rgba(28, 6, 20, 0.05),
    0 14px 34px -10px rgba(28, 6, 20, 0.16);
}

.pt-field--scrolled {
  mask-image: linear-gradient(to right, transparent 0, #000 16px);
}

.pt-send {
  transition:
    transform 200ms cubic-bezier(0.34, 1.56, 0.64, 1),
    opacity 220ms cubic-bezier(0.4, 0, 0.2, 1),
    box-shadow 220ms cubic-bezier(0.4, 0, 0.2, 1);
}

.pt-send--active {
  box-shadow: 0 8px 20px -8px rgba(44, 10, 32, 0.5);
}

.pt-caret--blink {
  animation: pt-blink 1.1s steps(1, end) infinite;
}

.pt-chip--in {
  animation: pt-chip-in 380ms cubic-bezier(0.34, 1.6, 0.64, 1) both;
}

.pt-chip--out {
  animation: pt-chip-out 220ms cubic-bezier(0.4, 0, 1, 1) both;
}

.pt-arrow--fly {
  animation: pt-arrow-fly 560ms cubic-bezier(0.4, 0, 0.2, 1) 1 both;
}

@keyframes pt-blink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
}

@keyframes pt-chip-in {
  0% {
    opacity: 0;
    transform: translateY(13px) scale(0.84);
    filter: blur(4px);
  }
  55% {
    opacity: 1;
    filter: blur(0);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
    filter: blur(0);
  }
}

@keyframes pt-chip-out {
  0% {
    opacity: 1;
    transform: translateY(0) scale(1);
    filter: blur(0);
  }
  100% {
    opacity: 0;
    transform: translateY(-10px) scale(0.94);
    filter: blur(3px);
  }
}

@keyframes pt-arrow-fly {
  0% {
    transform: translateY(0);
    opacity: 1;
  }
  34% {
    transform: translateY(-22px);
    opacity: 0;
  }
  42% {
    transform: translateY(14px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .pt-caret,
  .pt-chip,
  .pt-arrow {
    animation: none !important;
  }

  .pt-send {
    transition: none;
  }
}
</style>
